import React from "react";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SendIcon from "@mui/icons-material/Send";

// --------- Size styles
const sizeStyles = {
  sm: { fontSize: "12px", padding: "2px 8px" },
  md: { fontSize: "14px", padding: "4px 12px" },
  lg: { fontSize: "16px", padding: "6px 16px" },
};

// --------- Bootstrap-like color palette
const BOOTSTRAP_COLORS = {
  primary: "#0d6efd",
  secondary: "#6c757d",
  success: "#198754",
  danger: "#dc3545",
  warning: "#ffc107",
  info: "#0dcaf0",
  dark: "#212529",
  light: "#f8f9fa",
};

// --------- Determine readable text color based on background
const textColorForBg = (bg) => {
  const hex = bg.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000" : "#fff";
};

// --------- Icon keywords map
const statusIcons = {
  pending: <AccessTimeIcon fontSize="small" />,
  inprogress: <AccessTimeIcon fontSize="small" />,
  review: <HourglassTopIcon fontSize="small" />,
  submitted: <SendIcon fontSize="small" />,
  rejected: <CancelIcon fontSize="small" />,
  cancel: <CancelIcon fontSize="small" />,
  failed: <CancelIcon fontSize="small" />,
  warning: <WarningAmberIcon fontSize="small" />,
  approved: <CheckCircleIcon fontSize="small" />,
  done: <DoneAllIcon fontSize="small" />,
  complete: <DoneAllIcon fontSize="small" />,
  default: <FactCheckIcon fontSize="small" />,
};

const getIconFromKey = (key) => {
  if (!key || typeof key !== "string") return null;
  const norm = key.toLowerCase().replace(/[^a-z]/g, "");
  return statusIcons[norm] || statusIcons.default;
};

// --------- Main StatusLabel component
const StatusLabel = ({
  label,
  color = "secondary",
  tooltip = "",
  size = "sm",
  icon = null, // string keyword or JSX element
  variant = "solid", // 'solid' | 'soft' | 'outlined'
  customStyle = {},
  className = "",
}) => {
  const baseColor = BOOTSTRAP_COLORS[color] || color;
  const textColor = textColorForBg(baseColor);
  const borderColor = baseColor;

  const baseStyle = {
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    ...sizeStyles[size],
    ...customStyle,
  };

  let style = {};
  if (variant === "solid") {
    style = {
      backgroundColor: baseColor,
      color: textColor,
    };
  } else if (variant === "soft") {
    style = {
      backgroundColor: baseColor + "15",
      color: baseColor,
      fontWeight:'600',
    };
  } else if (variant === "outlined") {
    style = {
      backgroundColor: "#fff",
      border: `1px solid ${borderColor}`,
      fontWeight: "600",
      color: baseColor,
    };
  }

  const finalStyle = { ...baseStyle, ...style };

  const resolvedIcon =
    typeof icon === "string" ? getIconFromKey(icon) : icon || null;

  const content = (
    <span className={`status-label ${className}`} style={finalStyle}>
      {resolvedIcon && <span>{resolvedIcon}</span>}
      {label}
    </span>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

export default StatusLabel;
